import { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';

import CustomModal from '@/components/CustomModal';
import { RadioButtonGroup } from '@/components/RadioButton';
import Select from '@/components/Select';
import { TEACHER_STUDENTS_STATUS_OPTIONS } from '@/constants/constant';
import { useFormatText } from '@/hooks/useFormatText';
import {
  getTeacherLessonDropdown,
  getTeacherStudentsDropdown,
} from '@/service/lessonService';
import { formatQuery } from '@/utils';

export interface IClassDetailsFilters {
  studentName: string | string[];
  lesson_id: string | string[];
  status: string;
}

interface IDropdownItem {
  id: string;
  name: string;
}

interface IDetailsFilterModalProps {
  isOpen: boolean;
  setIsOpen: (isOpen: boolean) => void;
  initialFilters?: IClassDetailsFilters | null;
  onApplyFilters?: (filters: IClassDetailsFilters) => void;
}

const ClassStudentFilterModal = ({
  isOpen,
  setIsOpen,
  initialFilters,
  onApplyFilters,
}: IDetailsFilterModalProps) => {
  const { formatText } = useFormatText();
  const params = useParams();

  const [studentOptions, setStudentOptions] = useState<
    { value: string; label: string }[]
  >([]);
  const [lessonOptions, setLessonOptions] = useState<
    { value: string; label: string }[]
  >([]);
  const [isStudentsLoading, setIsStudentsLoading] = useState(false);
  const [isLessonsLoading, setIsLessonsLoading] = useState(false);

  const [studentName, setStudentName] = useState<string | string[]>(
    initialFilters?.studentName || []
  );
  const [lesson_id, setLesson_id] = useState<string | string[]>(
    initialFilters?.lesson_id || []
  );
  const [status, setStatus] = useState<string>(initialFilters?.status || '');

  useEffect(() => {
    fetchStudents();
    fetchLessons();
    //eslint-disable-next-line react-hooks/exhaustive-deps
  }, [params.id]);

  const fetchStudents = async () => {
    if (!params.id) return;
    setIsStudentsLoading(true);
    try {
      const queryString = formatQuery({
        class_id: params.id,
        exclude_block: true,
      });
      const res = await getTeacherStudentsDropdown(queryString);
      if (res?.status && res?.data) {
        setStudentOptions(
          res.data.map((item: IDropdownItem) => ({
            value: item.id,
            label: item.name,
          }))
        );
      }
    } catch (error) {
      console.error(error);
    } finally {
      setIsStudentsLoading(false);
    }
  };

  const fetchLessons = async () => {
    if (!params.id) return;
    setIsLessonsLoading(true);
    try {
      const queryString = formatQuery({ class_id: params.id });

      const res = await getTeacherLessonDropdown(queryString);
      if (res?.status && res?.data) {
        setLessonOptions(
          res.data.map((item: IDropdownItem) => ({
            value: item.id,
            label: item.name,
          }))
        );
      }
    } catch (error) {
      console.error(error);
    } finally {
      setIsLessonsLoading(false);
    }
  };

  const handleClearFilters = () => {
    setStudentName([]);
    setLesson_id([]);
    setStatus('');
    onApplyFilters?.({
      studentName: [],
      lesson_id: [],
      status: '',
    });
    setIsOpen(false);
  };

  const handleApplyFilters = () => {
    onApplyFilters?.({
      studentName,
      lesson_id,
      status,
    });
    setIsOpen(false);
  };

  if (!isOpen) return null;

  return (
    <CustomModal
      setIsOpen={setIsOpen}
      position="right"
      isHeader
      heading={formatText('common.filter')}
      isFooter
      cancelButtonProps={{
        variant: 'link',
        label: formatText('common.clear-filters'),
        onClick: handleClearFilters,
      }}
      confirmButtonProps={{
        label: formatText('common.show-results'),
        onClick: handleApplyFilters,
        variant: 'primary',
      }}
    >
      <div className="flex flex-col gap-6 w-full">
        <Select
          name={formatText('teacher-classes.details.students')}
          options={studentOptions}
          selected={studentName}
          onChange={setStudentName}
          placeholder={formatText(
            'teacher-classes.details.student-placeholder'
          )}
          className="w-full"
          isMulti
          isClearable
          isLoading={isStudentsLoading}
        />

        <Select
          name={formatText('class-details.filters-current-lesson')}
          options={lessonOptions}
          selected={lesson_id}
          onChange={setLesson_id}
          placeholder={formatText('teacher-classes.details.lesson-placeholder')}
          className="w-full"
          isMulti
          isClearable
          isLoading={isLessonsLoading}
        />

        <RadioButtonGroup
          name="status"
          label={formatText('common.status-label')}
          options={TEACHER_STUDENTS_STATUS_OPTIONS}
          value={status}
          onChange={setStatus}
          direction="row"
          gap="gap-8"
          formatLabel={formatText}
        />
      </div>
    </CustomModal>
  );
};

export default ClassStudentFilterModal;
